import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Newspaper, Calendar, ChevronLeft, ChevronRight, X, ArrowRight, Share2, Copy, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface NewsItem {
  id: string;
  title: string;
  content: string;
  image_url: string | null;
  images: string[] | null;
  video_url: string | null;
  news_date: string | null;
  created_at: string;
}

const News = () => {
  const { toast } = useToast();
  const { data: news, isLoading } = useQuery({
    queryKey: ["family-news"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("family_news")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as NewsItem[];
    },
  });

  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
  const [lightbox, setLightbox] = useState<{ images: string[]; index: number } | null>(null);
  const [copied, setCopied] = useState(false);

  const shareWhatsApp = (item: NewsItem) => {
    const text = `📰 ${item.title}\n\n${item.content.slice(0, 200)}${item.content.length > 200 ? '...' : ''}\n\n${window.location.href}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    toast({ title: "تم نسخ الرابط" });
    setTimeout(() => setCopied(false), 2000);
  };

  const getMainImage = (item: NewsItem) => {
    return item.image_url || (item.images?.length ? item.images[0] : null);
  };

  return (
    <div className="min-h-screen pt-20 md:pt-24 pb-12 px-3 md:px-4">
      <div className="container mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="font-amiri text-4xl md:text-5xl font-bold gold-text mb-4">أخبار العائلة</h1>
          <div className="w-24 h-[2px] gold-gradient rounded-full mx-auto" />
        </motion.div>

        {isLoading ? (
          <div className="text-center text-muted-foreground">جارٍ التحميل...</div>
        ) : !news?.length ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <Newspaper className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground text-lg">لا توجد أخبار حالياً</p>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {news.map((item, i) => {
              const mainImage = getMainImage(item);
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Card
                    className="glass-card border-primary/10 hover:border-primary/30 transition-all cursor-pointer overflow-hidden group h-full flex flex-col"
                    onClick={() => setSelectedNews(item)}
                  >
                    <div className="w-full h-40 sm:h-48 overflow-hidden bg-secondary/30">
                      {mainImage ? (
                        <img src={mainImage} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Newspaper className="h-12 w-12 text-muted-foreground/20" />
                        </div>
                      )}
                    </div>
                    <CardContent className="p-3 sm:p-4 flex-1 flex flex-col">
                      <div className="flex items-center gap-2 text-muted-foreground text-xs mb-2">
                        <Calendar className="h-3 w-3" />
                        <span>{new Date(item.news_date || item.created_at).toLocaleDateString("ar-SA")}</span>
                      </div>
                      <h2 className="font-amiri text-base sm:text-lg font-bold text-primary mb-2 line-clamp-2">{item.title}</h2>
                      <p className="text-muted-foreground text-sm line-clamp-2 flex-1">{item.content}</p>
                      <Button variant="ghost" size="sm" className="mt-3 text-primary hover:text-primary/80 self-start p-0 h-auto">
                        عرض التفاصيل
                        <ArrowRight className="h-3 w-3 mr-1 rotate-180" />
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* News Detail Modal */}
      <AnimatePresence>
        {selectedNews && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-start justify-center p-2 sm:p-4 pt-16 sm:pt-20 overflow-y-auto"
            onClick={() => setSelectedNews(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 30 }}
              className="glass-card border border-primary/20 rounded-2xl max-w-3xl w-full mb-10 overflow-hidden max-h-[85vh] sm:max-h-none flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center p-3 sm:p-4 border-b border-border/50 shrink-0">
                <h2 className="font-amiri text-base sm:text-xl font-bold text-primary line-clamp-1 flex-1 ml-2">{selectedNews.title}</h2>
                <button onClick={() => setSelectedNews(null)} className="text-muted-foreground hover:text-foreground p-1 shrink-0">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="overflow-y-auto flex-1">
              {/* Images */}
              {(() => {
                const images: string[] = selectedNews.images?.length ? selectedNews.images : (selectedNews.image_url ? [selectedNews.image_url] : []);
                return images.length > 0 && (
                  <div className={images.length === 1 ? "" : "grid grid-cols-2 md:grid-cols-3 gap-1"}>
                    {images.length === 1 ? (
                      <div className="w-full max-h-60 sm:max-h-96 overflow-hidden cursor-pointer" onClick={() => setLightbox({ images, index: 0 })}>
                        <img src={images[0]} alt={selectedNews.title} className="w-full h-full object-cover" />
                      </div>
                    ) : (
                      images.slice(0, 4).map((url, idx) => (
                        <div
                          key={idx}
                          className={`relative overflow-hidden cursor-pointer h-28 sm:h-36 ${idx === 0 && images.length <= 3 ? 'col-span-2 md:col-span-1' : ''}`}
                          onClick={() => setLightbox({ images, index: idx })}
                        >
                          <img src={url} alt={`${selectedNews.title} ${idx + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
                          {idx === 3 && images.length > 4 && (
                            <div className="absolute inset-0 bg-background/60 flex items-center justify-center">
                              <span className="text-foreground text-2xl font-bold">+{images.length - 4}</span>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                );
              })()}

              {/* Video */}
              {selectedNews.video_url && (
                <div className="w-full px-4 pt-4">
                  <video src={selectedNews.video_url} controls className="w-full max-h-80 rounded-xl" preload="metadata" />
                </div>
              )}

              <div className="p-4 sm:p-6">
                <div className="flex items-center gap-2 text-muted-foreground text-xs sm:text-sm mb-3 sm:mb-4">
                  <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span>{new Date(selectedNews.news_date || selectedNews.created_at).toLocaleDateString("ar-SA")}</span>
                </div>
                <p className="text-foreground leading-relaxed text-sm sm:text-lg whitespace-pre-line">{selectedNews.content}</p>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-border/50">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-xl border-primary/20 text-primary hover:bg-primary/10 gap-2 text-xs sm:text-sm"
                    onClick={() => shareWhatsApp(selectedNews)}
                  >
                    <Share2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    مشاركة عبر واتساب
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-xl border-primary/20 text-primary hover:bg-primary/10 gap-2 text-xs sm:text-sm"
                    onClick={copyLink}
                  >
                    {copied ? <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> : <Copy className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
                    {copied ? "تم النسخ" : "نسخ الرابط"}
                  </Button>
                </div>
              </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-background/90 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
          >
            <button className="absolute top-4 right-4 text-foreground p-2" onClick={() => setLightbox(null)}>
              <X className="h-6 w-6" />
            </button>
            <img
              src={lightbox.images[lightbox.index]}
              alt=""
              className="max-w-full max-h-[85vh] object-contain rounded-xl"
              onClick={(e) => e.stopPropagation()}
            />
            {lightbox.images.length > 1 && (
              <>
                <button
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-primary/20 hover:bg-primary/40 rounded-full p-2 text-foreground"
                  onClick={(e) => { e.stopPropagation(); setLightbox(l => l ? { ...l, index: (l.index - 1 + l.images.length) % l.images.length } : null); }}
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
                <button
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-primary/20 hover:bg-primary/40 rounded-full p-2 text-foreground"
                  onClick={(e) => { e.stopPropagation(); setLightbox(l => l ? { ...l, index: (l.index + 1) % l.images.length } : null); }}
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <div className="absolute bottom-6 text-foreground text-sm">
                  {lightbox.index + 1} / {lightbox.images.length}
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default News;
